import React, { Component } from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import { Menu, Icon } from 'antd'
import LangContext from 'modules/shared/context/langContext'
import { AutorizeMenu } from '../../shared/components/CustomMenu'
import CustomMenuItem from '../../shared/components/CustomMenuItem'

import { autoGenKey } from '../../../lib/helper'

const rootSubmenuKeys = [
  'dashboard-sub-menu',
  'report-sub-menu',
  'fleetmanagement-sub-menu',
  'monitoring-sub-menu',
  'safety-dashboard-sub-menu',
  'management_submenu',
  'masterData_submenu',
  'deliver_submenu',
  'help_menu_submenu',
  'admin-gps-submenu',
  'route-optimize-submenu',
  'setting_submenu',
  'legacy-sub-menu',
]

// Temporary toggle to hide legacy menus (easy to re-enable)
const SHOW_LEGACY_MENU = false
class SliderBar extends Component {
  static contextType = LangContext

  state = {
    openKeys: [],
    isActive: '',
    web_endpoint: '',
  }

  componentDidMount() {
    this.setState({ web_endpoint: process.env.REACT_APP_WEB_ENDPOINT })
  }

  onOpenChange = openKeys => {
    const nextOpenKeys = (openKeys || []).filter(Boolean)
    const latestOpenKey = nextOpenKeys.find(
      key => this.state.openKeys.indexOf(key) === -1,
    )

    // If user opens a root submenu, keep only that root open.
    if (rootSubmenuKeys.indexOf(latestOpenKey) !== -1) {
      this.setState({ openKeys: latestOpenKey ? [latestOpenKey] : [] })
      return
    }

    // For nested submenus under a root: keep only [root, latestNested]
    if (nextOpenKeys.length > 2) {
      this.setState({
        openKeys: [nextOpenKeys[0], nextOpenKeys[nextOpenKeys.length - 1]],
      })
      return
    }

    this.setState({ openKeys: nextOpenKeys })
  }

  handleActiveMenu = key => {
    this.setState({ isActive: key })
  }

  shouldComponentUpdate() {
    if (this.props.countAppoveRequestToUseVehicle) {
      return true
    }
    return true
  }

  createExternalMenu = (permissions, menu_hierarchy, opts) => {
    if (!permissions || !Array.isArray(permissions)) {
      return null
    }
    opts = opts || {}

    const externalPermissions = permissions.filter(
      permission =>
        permission.url_type &&
        permission.url_type === 2 &&
        permission.web_use === 'fleetlink' &&
        permission.can_view === 1,
    )

    // Debug: inspect backend permission payload (dev only)
    if (
      typeof process !== 'undefined' &&
      process.env &&
      process.env.NODE_ENV !== 'production'
    ) {
      try {
        // Avoid spamming: log once per root
        this._debugLoggedExternalMenuRoots =
          this._debugLoggedExternalMenuRoots || {}
        if (!this._debugLoggedExternalMenuRoots[menu_hierarchy]) {
          this._debugLoggedExternalMenuRoots[menu_hierarchy] = true
          const sample = externalPermissions.slice(0, 5)
        }
      } catch (e) {
        // ignore
      }
    }

    // Support nested external menus by parsing menu_hierarchy paths like:
    // "monitoring > video" or ["monitoring","video"] (if backend returns array)
    const normalizePath = p => {
      const raw = p ? p.menu_hierarchy : ''
      if (Array.isArray(raw)) return raw.map(x => `${x}`.trim()).filter(Boolean)
      if (typeof raw === 'string') {
        return raw
          .split('>')
          .map(x => x.trim())
          .filter(Boolean)
      }
      return []
    }

    const normalizeMenuArg = arg => {
      if (Array.isArray(arg)) return arg.map(x => `${x}`.trim()).filter(Boolean)
      if (typeof arg === 'string') {
        return arg
          .split('>')
          .map(x => x.trim())
          .filter(Boolean)
      }
      return []
    }

    const rootSegments = normalizeMenuArg(menu_hierarchy)

    // Only include permissions that belong under this root menu_hierarchy
    const inRoot = p => {
      const path = normalizePath(p)
      if (rootSegments.length > 0) {
        if (path.length < rootSegments.length) return false
        for (let i = 0; i < rootSegments.length; i++) {
          if (path[i] !== rootSegments[i]) return false
        }
        return true
      }
      // fallback for older payloads where menu_hierarchy is just the root key
      return p && p.menu_hierarchy === menu_hierarchy
    }

    const permsInRoot = externalPermissions.filter(inRoot).filter(p => {
      // If we're rendering at a root level and that next segment already exists
      // as a real submenu, skip it here (it will be injected into that submenu).
      if (
        rootSegments.length === 1 &&
        opts.skipNextSegments &&
        Array.isArray(opts.skipNextSegments)
      ) {
        const path = normalizePath(p)
        const next = path.length > 1 ? path[1] : ''
        if (next && opts.skipNextSegments.indexOf(next) !== -1) return false
      }
      return true
    })

    // Debug: show which items got assigned under this root (dev only)
    if (
      typeof process !== 'undefined' &&
      process.env &&
      process.env.NODE_ENV !== 'production'
    ) {
      try {
        this._debugLoggedExternalMenuAssigned =
          this._debugLoggedExternalMenuAssigned || {}
        if (!this._debugLoggedExternalMenuAssigned[menu_hierarchy]) {
          this._debugLoggedExternalMenuAssigned[menu_hierarchy] = true
          const assigned = permsInRoot.slice(0, 20).map(p => ({
            matching_name: p.matching_name,
            name: p.name,
            url: p.url,
            menu_hierarchy: p.menu_hierarchy,
            parsedPath: normalizePath(p),
          }))
        }
      } catch (e) {
        // ignore
      }
    }

    // Build a tree: nodes can be { type:'group', key, labelKey, children:[] } or { type:'item', perm }
    const root = {
      type: 'group',
      key: `external-root-${menu_hierarchy}`,
      children: [],
    }

    const getOrCreateGroup = (children, groupKey, labelKey) => {
      let found = children.find(
        n => n && n.type === 'group' && n.key === groupKey,
      )
      if (found) return found
      const node = { type: 'group', key: groupKey, labelKey, children: [] }
      children.push(node)
      return node
    }

    permsInRoot.forEach(p => {
      const path = normalizePath(p)
      // remove the matched prefix segments (e.g. ["monitoring"] or ["monitoring","video"])
      const segments =
        rootSegments.length > 0
          ? path.slice(rootSegments.length)
          : path.slice(1)

      // If no segments, render as a direct Menu.Item under the root submenu
      if (segments.length === 0) {
        root.children.push({ type: 'item', perm: p })
        return
      }

      let cursor = root
      segments.forEach((seg, idx) => {
        const isLeaf = idx === segments.length - 1
        if (isLeaf) {
          // Leaf group contains the menu item(s)
          const groupKey = `external-${menu_hierarchy}-${segments
            .slice(0, idx + 1)
            .join('>')}`
          const group = getOrCreateGroup(cursor.children, groupKey, seg)
          group.children.push({ type: 'item', perm: p })
        } else {
          const groupKey = `external-${menu_hierarchy}-${segments
            .slice(0, idx + 1)
            .join('>')}`
          cursor = getOrCreateGroup(cursor.children, groupKey, seg)
        }
      })
    })

    const renderItem = p => (
      <Menu.Item
        key={`external-item-${p.matching_name}`}
        matching_name={p.matching_name}
        web_endpoint={this.state.web_endpoint}
        web_use="fleetlink"
        style={{
          backgroundColor:
            this.state.isActive === p.matching_name && '#1890FF70',
        }}
      >
        <CustomMenuItem
          matching_name={p.matching_name}
          permissions={permissions}
          external
        >
          <Link to={p.url}>
            <span className="nav-text">{p.name}</span>
          </Link>
        </CustomMenuItem>
      </Menu.Item>
    )

    const renderNode = node => {
      if (!node) return null
      if (node.type === 'item') return renderItem(node.perm)
      if (node.type === 'group') {
        return (
          <Menu.SubMenu
            key={node.key}
            popupClassName="popup-menu-custom scroll-style"
            title={
              <LangContext.Consumer>
                {i18n => i18n.menuHierarchy[node.labelKey] || node.labelKey}
              </LangContext.Consumer>
            }
          >
            {(node.children || []).map(child => renderNode(child))}
          </Menu.SubMenu>
        )
      }
      return null
    }

    return (root.children || []).map(child => renderNode(child))
  }

  render() {
    const { collapsed } = this.props

    const { permissions } = this.props.profile

    let checkPermissionShowCountNoti = permissions.find(
      item => item.matching_name === 'appoveToUseVehicle',
    )

    let countAppoveRequest = localStorage.getItem(
      'count-appove-request-vehicle',
    )

    // *----------------แจ้งเตือนเมนูช่วยเหลือ----------------*
    // แจ้งติดตั้ง
    let checkPermissionShowCountGPSServiceRequestsInstall = permissions.find(
      item => item.matching_name === 'gpsInstallation',
    )

    let countGPSServiceRequestsInstall =
      localStorage.getItem('count-gps-service-requests-install') || 0

    // แจ้งซ่อม
    let checkPermissionShowCountGPSServiceRequestsRepair = permissions.find(
      item => item.matching_name === 'gpsRepair',
    )

    let countGPSServiceRequestsRepair =
      localStorage.getItem('count-gps-service-requests-repair') || 0

    // แจ้งถอด
    let checkPermissionShowCountGPSServiceRequestsUninstall = permissions.find(
      item => item.matching_name === 'gpsUninstallation',
    )

    let countGPSServiceRequestsUninstall =
      localStorage.getItem('count-gps-service-requests-uninstall') || 0

    // ประสิทธิภาพอุปกรณ์รายเดือน
    let checkPermissionShowCountGpsMonthlyUsage = permissions.find(
      item => item.matching_name === 'gpsMonthlyUsage',
    )

    let countGpsMonthlyUsage =
      localStorage.getItem('count-gps-monthly-usage') || 0

    // รวมแจ้งเตือนสำหรับเมนูช่วยเหลือ
    let countNumberNotificationHelper = 0
    if (
      countGPSServiceRequestsInstall != 0 &&
      checkPermissionShowCountGPSServiceRequestsInstall != undefined &&
      checkPermissionShowCountGPSServiceRequestsInstall.can_view == 1
    ) {
      countNumberNotificationHelper += parseInt(countGPSServiceRequestsInstall)
    }

    if (
      countGPSServiceRequestsRepair != 0 &&
      checkPermissionShowCountGPSServiceRequestsRepair != undefined &&
      checkPermissionShowCountGPSServiceRequestsRepair.can_view == 1
    ) {
      countNumberNotificationHelper += parseInt(countGPSServiceRequestsRepair)
    }

    if (
      countGPSServiceRequestsUninstall != 0 &&
      checkPermissionShowCountGPSServiceRequestsUninstall != undefined &&
      checkPermissionShowCountGPSServiceRequestsUninstall.can_view == 1
    ) {
      countNumberNotificationHelper += parseInt(
        countGPSServiceRequestsUninstall,
      )
    }

    // *----------------แจ้งเตือนเมนูช่วยเหลือ----------------*

    return (
      <Menu
        mode="inline"
        theme="light"
        // inlineIndent={0}
        className="ant-side-menu"
        selectable={false}
      >
        {/* ระบบติดตาม */}
        <AutorizeMenu
          isActive={this.state.isActive}
          handleActiveMenu={this.handleActiveMenu}
          permissions={permissions}
          title={
            <span>
              <Icon
                type="car"
                style={{ fontSize: 'larger', color: '#1890FF' }}
              />
              <span>
                {' '}
                <LangContext.Consumer>
                  {i18n => i18n.v.vehicleTracking}
                </LangContext.Consumer>
              </span>
            </span>
          }
          subkey={'monitoring-sub-menu'}
          handleonOpenChange={this.onOpenChange}
          openKeysMenu={this.state.openKeys}
          menu_hierarchy="monitoring"
          createExternalMenu={this.createExternalMenu}
        >
          {/* การจัดส่งคอนกรีต */}
          {/* <Menu.Item
            key={autoGenKey('slider-sub-menu')}
            matching_name="concreteDeliveryManagerDashboard"
            web_endpoint={this.state.web_endpoint}
            web_use=""
            style={{
              backgroundColor:
                this.state.isActive === 'concreteDeliveryManagerDashboard' &&
                '#1890FF70',
            }}
          >
            <CustomMenuItem
              matching_name="concreteDeliveryManagerDashboard"
              permissions={permissions}
            >
              <Link to="/concrete_delivery_manager">
                <span className="nav-text font_menu_item_slider_bar">
                  <LangContext.Consumer>
                    {i18n => i18n.c.concreteDeliveryManagerDashboard}
                  </LangContext.Consumer>
                </span>
              </Link>
            </CustomMenuItem>
          </Menu.Item> */}

          {/* การจัดส่งคอนกรีต v2*/}
          <Menu.Item
            key={autoGenKey('slider-sub-menu')}
            matching_name="concreteDeliveryManagerDashboardV2"
            web_endpoint={this.state.web_endpoint}
            web_use=""
            style={{
              backgroundColor:
                this.state.isActive === 'concreteDeliveryManagerDashboardV2' &&
                '#1890FF70',
            }}
          >
            <CustomMenuItem
              matching_name="concreteDeliveryManagerDashboardV2"
              permissions={permissions}
            >
              <Link to="/concrete-delivery-manager">
                <span className="nav-text font_menu_item_slider_bar">
                  <LangContext.Consumer>
                    {i18n => i18n.c.concreteDeliveryManagerDashboardV2}
                  </LangContext.Consumer>
                </span>
              </Link>
            </CustomMenuItem>
          </Menu.Item>
          {/* ติดตามรถ */}
          <Menu.Item
            key={autoGenKey('slider-sub-menu')}
            matching_name="tracking"
            web_endpoint={this.state.web_endpoint}
            web_use=""
            style={{
              backgroundColor:
                this.state.isActive === 'tracking' && '#1890FF70',
            }}
          >
            <CustomMenuItem matching_name="tracking" permissions={permissions}>
              <Link to="/tracking">
                <span className="nav-text">
                  <LangContext.Consumer>
                    {i18n => i18n.t.tracking}
                  </LangContext.Consumer>
                </span>
              </Link>
            </CustomMenuItem>
          </Menu.Item>

          {/* monitor gps */}
          <Menu.Item
            key={autoGenKey('slider-sub-menu')}
            matching_name="monitorGPS"
            web_endpoint={this.state.web_endpoint}
            web_use=""
            style={{
              backgroundColor:
                this.state.isActive === 'monitorGPS' && '#1890FF70',
            }}
          >
            <CustomMenuItem
              matching_name="monitorGPS"
              permissions={permissions}
            >
              <Link to="/monitor-gps">
                <span className="nav-text">
                  <LangContext.Consumer>
                    {i18n => i18n.m.monitorGPS}
                  </LangContext.Consumer>
                </span>
              </Link>
            </CustomMenuItem>
          </Menu.Item>

          {/* เส้นทางเดินทางย้อนหลัง */}
          <Menu.Item
            key={autoGenKey('slider-sub-menu')}
            matching_name="replay"
            web_endpoint={this.state.web_endpoint}
            web_use=""
            style={{
              backgroundColor: this.state.isActive === 'replay' && '#1890FF70',
            }}
          >
            <CustomMenuItem matching_name="replay" permissions={permissions}>
              <Link to="/replay">
                <span className="nav-text">
                  <LangContext.Consumer>
                    {i18n => i18n.r.replay}
                  </LangContext.Consumer>
                </span>
              </Link>
            </CustomMenuItem>
          </Menu.Item>

          {/* Honey Toast */}
          <Menu.SubMenu
            popupClassName="popup-menu-custom scroll-style"
            key={'honeyToast_1'}
            menu_hierarchy="video"
            permissions={permissions}
            title={
              <LangContext.Consumer>
                {i18n => i18n.menuHierarchy.video}
              </LangContext.Consumer>
            }
          >
            {/* วิดีโอเรียลไทม์จาก Honey Toast */}
            <Menu.Item
              className={collapsed ? '' : 'menu-item-wrap'}
              key={autoGenKey('slider-sub-menu')}
              matching_name="honeytoastlivestream"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'honeytoastlivestream' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="honeytoastlivestream"
                permissions={permissions}
              >
                <Link to="/honeytoastlivestream">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.h.honeytoastlivestream}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* วิดีโอเรียลไทม์ V2 (ยิง stream ตรง) */}
            <Menu.Item
              className={collapsed ? '' : 'menu-item-wrap'}
              key={autoGenKey('slider-sub-menu')}
              matching_name="honeytoastlivestreamV2"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'honeytoastlivestreamV2' &&
                  '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="honeytoastlivestreamV2"
                permissions={permissions}
              >
                <Link to="/honeytoastlivestream-v2">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.h.honeytoastlivestreamV2}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* วิดีโอย้อนหลัง Honey Toast */}
            <Menu.Item
              className={collapsed ? '' : 'menu-item-wrap'}
              key={autoGenKey('slider-sub-menu')}
              matching_name="honeytoastplayback"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'honeytoastplayback' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="honeytoastplayback"
                permissions={permissions}
              >
                <Link to="/honeytoastplaybackwebpage">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.h.honeytoastplayback}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* ดาวน์โหลดวิดีโอ Honey Toast */}
            <Menu.Item
              className={collapsed ? '' : 'menu-item-wrap'}
              key={autoGenKey('slider-sub-menu')}
              matching_name="honeytoastdownloadfile"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'honeytoastdownloadfile' &&
                  '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="honeytoastdownloadfile"
                permissions={permissions}
              >
                <Link to="/honeytoastdownloadfilewebpage">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.h.honeytoastdownloadfile}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* ประสิทธิภาพกล้อง */}
            <Menu.Item
              className={collapsed ? '' : 'menu-item-wrap'}
              key={autoGenKey('slider-sub-menu')}
              matching_name="cameraCheckStatus"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'cameraCheckStatus' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="cameraCheckStatus"
                permissions={permissions}
              >
                <Link to="/camera-performance">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.c.cameraCheckStatusPage}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>
          </Menu.SubMenu>
        </AutorizeMenu>
        {/* end ระบบติดตาม */}

        {/* ความปลอดภัย */}
        <AutorizeMenu
          isActive={this.state.isActive}
          handleActiveMenu={this.handleActiveMenu}
          permissions={permissions}
          title={
            <span>
              <Icon
                type="safety"
                style={{ fontSize: 'larger', color: '#1890FF' }}
              />
              <span>
                {' '}
                <LangContext.Consumer>
                  {i18n => i18n.s.safetyDashboard}
                </LangContext.Consumer>
              </span>
              {/* </Badge> */}
            </span>
          }
          subkey={'safety-dashboard-sub-menu'}
          handleonOpenChange={this.onOpenChange}
          openKeysMenu={this.state.openKeys}
          menu_hierarchy="safetyDashboard"
          createExternalMenu={this.createExternalMenu}
        >
          {/* ความปลอดภัย */}
          <Menu.Item
            key={autoGenKey('slider-sub-menu')}
            matching_name="SafetyDashboard"
            web_endpoint={this.state.web_endpoint}
            web_use=""
            style={{
              backgroundColor:
                this.state.isActive === 'SafetyDashboard' && '#1890FF70',
              color: this.state.isActive === 'SafetyDashboard' && 'white',
            }}
          >
            <CustomMenuItem
              matching_name="SafetyDashboard"
              permissions={permissions}
            >
              <Link to="/safety_dashboard">
                <span className="nav-text">
                  <LangContext.Consumer>
                    {i18n => i18n.s.safetyDashboard}
                  </LangContext.Consumer>
                </span>
              </Link>
            </CustomMenuItem>
          </Menu.Item>
          <Menu.Item
            key={autoGenKey('slider-sub-menu')}
            matching_name="cpacPolicy"
            web_endpoint={this.state.web_endpoint}
            web_use=""
            style={{
              backgroundColor:
                this.state.isActive === 'cpacPolicy' && '#1890FF70',
            }}
          >
            <CustomMenuItem
              matching_name="cpacPolicy"
              permissions={permissions}
            >
              <Link to="/cpac-policy">
                <span className="nav-text">
                  <LangContext.Consumer>
                    {i18n => i18n.c.cpacPolicy}
                  </LangContext.Consumer>
                </span>
              </Link>
            </CustomMenuItem>
          </Menu.Item>
          {/* รายงาน */}
          <Menu.SubMenu
            popupClassName="popup-menu-custom scroll-style"
            key={'safety-dashboard-report-sub-menu'}
            menu_hierarchy="report"
            permissions={permissions}
            title={
              <LangContext.Consumer>
                {i18n => i18n.r.report}
              </LangContext.Consumer>
            }
          >
            {/* สรุปพฤติกรรมการขับรถของ เจ้าหน้าที่บริการและจัดส่ง */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="BehaviordrivingReport"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              className={collapsed ? '' : 'menu-item-wrap'}
              style={{
                backgroundColor:
                  this.state.isActive === 'BehaviordrivingReport' &&
                  '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="BehaviordrivingReport"
                permissions={permissions}
              >
                <Link to="/report/behaviordriving">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.b.BehaviordrivingReport}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* สรุปพฤติกรรมการขับรถของ เจ้าหน้าที่บริการและจัดส่ง แต่ละผู้รับเหมา */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="BehaviordrivingbycompanyReport"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              className={collapsed ? '' : 'menu-item-wrap'}
              style={{
                backgroundColor:
                  this.state.isActive === 'BehaviordrivingbycompanyReport' &&
                  '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="BehaviordrivingbycompanyReport"
                permissions={permissions}
              >
                <Link to="/report/behaviordrivingbycompany">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.b.BehaviordrivingbycompanyReport}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* รูปแบบการสรุปคะแนน */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="ScoresummaryReport"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              className={collapsed ? '' : 'menu-item-wrap'}
              style={{
                backgroundColor:
                  this.state.isActive === 'ScoresummaryReport' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="ScoresummaryReport"
                permissions={permissions}
              >
                <Link to="/report/scoresummary">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.s.ScoresummaryReport}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* ความปลอดภัยพาหนะ */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="SafetyEventReport"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              className={collapsed ? '' : 'menu-item-wrap'}
              style={{
                backgroundColor:
                  this.state.isActive === 'SafetyEventReport' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="SafetyEventReport"
                permissions={permissions}
              >
                <Link to="/report/safety_event">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.e.SafetyEventReport}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>
          </Menu.SubMenu>
        </AutorizeMenu>

        {/* รายงาน */}
        <AutorizeMenu
          isActive={this.state.isActive}
          handleActiveMenu={this.handleActiveMenu}
          permissions={permissions}
          title={
            <span>
              <Icon
                type="exception"
                style={{ fontSize: 'larger', color: '#1890FF' }}
              />
              <span>
                {' '}
                <LangContext.Consumer>
                  {i18n => i18n.r.report}
                </LangContext.Consumer>
              </span>
            </span>
          }
          subkey={'report-sub-menu'}
          handleonOpenChange={this.onOpenChange}
          openKeysMenu={this.state.openKeys}
          menu_hierarchy="report"
          createExternalMenu={this.createExternalMenu}
        >
          <Menu.Item
            key={autoGenKey('slider-sub-menu')}
            matching_name="schedulereport"
            web_endpoint={this.state.web_endpoint}
            web_use=""
            className={collapsed ? '' : 'menu-item-wrap'}
            style={{
              backgroundColor:
                this.state.isActive === 'schedulereport' && '#1890FF70',
            }}
          >
            <CustomMenuItem
              matching_name="schedulereport"
              permissions={permissions}
            >
              <Link to="/schedulereport">
                <span className="nav-text">
                  <LangContext.Consumer>
                    {i18n => i18n.s.schedulereport}
                  </LangContext.Consumer>
                </span>
              </Link>
            </CustomMenuItem>
          </Menu.Item>

          {/* ภาพรวม Overview */}
          <Menu.SubMenu
            popupClassName="popup-menu-custom scroll-style"
            key={'overview-report-sub-menu'}
            menu_hierarchy="overview"
            permissions={permissions}
            title={
              <LangContext.Consumer>
                {i18n => i18n.o.overviewReportMenu}
              </LangContext.Consumer>
            }
          >
            {/* แดชบอร์ดภาพรวมการขนส่งคอนกรีต */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="truckstatusdashboard"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'truckstatusdashboard' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="truckstatusdashboard"
                permissions={permissions}
              >
                <Link to="/truckstatusdashboard">
                  <span className="nav-text font_menu_item_slider_bar">
                    <LangContext.Consumer>
                      {i18n => i18n.t.truckstatusdashboard}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>
            {/* สรุปการใช้พาหนะ */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="driverKpiDashboard"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'driverKpiDashboard' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="driverKpiDashboard"
                permissions={permissions}
              >
                <Link to="/driver-kpi">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.d.driverKpiDashboard}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>
            {/* สรุปประสิทธิภาพพาหนะรายคัน */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="VehicleperformanceconcludeReport"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              className={collapsed ? '' : 'menu-item-wrap'}
              style={{
                backgroundColor:
                  this.state.isActive === 'VehicleperformanceconcludeReport' &&
                  '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="VehicleperformanceconcludeReport"
                permissions={permissions}
              >
                <Link to="/report/vehicleperformanceconclude">
                  <span className="nav-text">
                    {' '}
                    <LangContext.Consumer>
                      {i18n => i18n.v.VehicleperformanceconcludeReport}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>
            {/* รายงานการขับขี่ (รายคัน) */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="drivingSummary"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              className={collapsed ? '' : 'menu-item-wrap'}
              style={{
                backgroundColor:
                  this.state.isActive === 'drivingSummary' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="drivingSummary"
                permissions={permissions}
              >
                <Link to="/report/driving-summary">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.d.drivingSummary}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>
            {/* สรุปสถานะพาหนะรายวัน */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="dailyCarStatusReport"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              className={collapsed ? '' : 'menu-item-wrap'}
              style={{
                backgroundColor:
                  this.state.isActive === 'dailyCarStatusReport' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="dailyCarStatusReport"
                permissions={permissions}
              >
                <Link to="/report/separatevehiclestatus">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.d.dailyCarStatusReport}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>
          </Menu.SubMenu>

          {/* ทั่วไป General */}
          <Menu.SubMenu
            popupClassName="popup-menu-custom scroll-style"
            key={'item_report_2'}
            menu_hierarchy="general"
            permissions={permissions}
            title={
              <LangContext.Consumer>
                {i18n => i18n.g.general_report_menu}
              </LangContext.Consumer>
            }
          >
            {/* จำนวนวันที่รถวิ่งงาน */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="NumberofdaysworkedReport"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              className={collapsed ? '' : 'menu-item-wrap'}
              style={{
                backgroundColor:
                  this.state.isActive === 'NumberofdaysworkedReport' &&
                  '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="NumberofdaysworkedReport"
                permissions={permissions}
              >
                <Link to="/report/numberofdaysworked">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.n.NumberofdaysworkedReport}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* สรุประยะทางการวิ่งประจำวัน */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="DrivingdistanceReport"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              className={collapsed ? '' : 'menu-item-wrap'}
              style={{
                backgroundColor:
                  this.state.isActive === 'DrivingdistanceReport' &&
                  '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="DrivingdistanceReport"
                permissions={permissions}
              >
                <Link to="/report/drivingdistance">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.d.DrivingdistanceReport}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="LoginhistoryReport"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              className={collapsed ? '' : 'menu-item-wrap'}
              style={{
                backgroundColor:
                  this.state.isActive === 'LoginhistoryReport' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="LoginhistoryReport"
                permissions={permissions}
              >
                <Link to="/report/loginhistory">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.l.LoginhistoryReport}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* การรูดใบขับขี่ */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="DrivinglicensescanlogReport"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              className={collapsed ? '' : 'menu-item-wrap'}
              style={{
                backgroundColor:
                  this.state.isActive === 'DrivinglicensescanlogReport' &&
                  '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="DrivinglicensescanlogReport"
                permissions={permissions}
              >
                <Link to="/report/drivinglicensescanlog">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.d.DrivinglicensescanlogReport}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* บันทึกการขับขี่ */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="DrivinglogReport"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              className={collapsed ? '' : 'menu-item-wrap'}
              style={{
                backgroundColor:
                  this.state.isActive === 'DrivinglogReport' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="DrivinglogReport"
                permissions={permissions}
              >
                <Link to="/report/drivinglog">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.d.DrivinglogReport}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* จุดจอดพาหนะ */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="ParkingReport"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              className={collapsed ? '' : 'menu-item-wrap'}
              style={{
                backgroundColor:
                  this.state.isActive === 'ParkingReport' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="ParkingReport"
                permissions={permissions}
              >
                <Link to="/report/parking">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.p.ParkingReport}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* การใช้งานพาหนะ */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="TruckusageReport"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              className={collapsed ? '' : 'menu-item-wrap'}
              style={{
                backgroundColor:
                  this.state.isActive === 'TruckusageReport' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="TruckusageReport"
                permissions={permissions}
              >
                <Link to="/report/truckusage">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.t.TruckusageReport}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>
            {/* สรุปการเดินทางแยกประเภทตามการเปิด-ปิดเครื่องยนต์ */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="TripbyengineoffonReport"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              className={collapsed ? '' : 'menu-item-wrap'}
              style={{
                backgroundColor:
                  this.state.isActive === 'TripbyengineoffonReport' &&
                  '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="TripbyengineoffonReport"
                permissions={permissions}
              >
                <Link to="/report/tripbyengineoffon">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.t.TripbyengineoffonReport}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>
            {/* พาหนะติดเครื่อง */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="TruckengineonReport"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              className={collapsed ? '' : 'menu-item-wrap'}
              style={{
                backgroundColor:
                  this.state.isActive === 'TruckengineonReport' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="TruckengineonReport"
                permissions={permissions}
              >
                <Link to="/report/truckengineon">
                  <span className="nav-text">
                    {' '}
                    <LangContext.Consumer>
                      {i18n => i18n.t.TruckengineonReport}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>
          </Menu.SubMenu>

          {/* ทริป */}
          <Menu.SubMenu
            popupClassName="popup-menu-custom scroll-style"
            key={'trip_report_menu_1'}
            menu_hierarchy="trip"
            permissions={permissions}
            title={
              <LangContext.Consumer>
                {i18n => i18n.t.trip_report_menu}
              </LangContext.Consumer>
            }
          >
            {/* การส่งคอนกรีต */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="concreteTrip"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              className={collapsed ? '' : 'menu-item-wrap'}
              style={{
                backgroundColor:
                  this.state.isActive === 'concreteTrip' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="concreteTrip"
                permissions={permissions}
              >
                <Link to="/report/rmcconcretetrip">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.c.concreteTrip}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>
            {/* รายงานเทปูนนอกจุด */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="unauthorizeunload"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              className={collapsed ? '' : 'menu-item-wrap'}
              style={{
                backgroundColor:
                  this.state.isActive === 'unauthorizeunload' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="unauthorizeunload"
                permissions={permissions}
              >
                <Link to="/report/unauthorizeunload">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.u.unauthorizeunload}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>
            {/* รายงานย้ายจุดอัตโนมัติ */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="automovingsite-report"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              className={collapsed ? '' : 'menu-item-wrap'}
              style={{
                backgroundColor:
                  this.state.isActive === 'automovingsite-report' &&
                  '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="automovingsite-report"
                permissions={permissions}
              >
                <Link to="/report/auto-move-site">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.a.autoMoveSite}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* ระยะทางการจัดส่งคอนกรีต */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="distancereport"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              className={collapsed ? '' : 'menu-item-wrap'}
              style={{
                backgroundColor:
                  this.state.isActive === 'distancereport' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="distancereport"
                permissions={permissions}
              >
                <Link to="/report/distancereport">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.d.distancereport}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>
          </Menu.SubMenu>

          {/* พลังงาน */}
          <Menu.SubMenu
            popupClassName="popup-menu-custom scroll-style"
            key={'energy_report_menu_1'}
            menu_hierarchy="energy"
            permissions={permissions}
            title={
              <LangContext.Consumer>
                {i18n => i18n.e.energy_report_menu}
              </LangContext.Consumer>
            }
          >
            {/* สรุปการใช้พาหนะ */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="summaryFuelUsage"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              className={collapsed ? '' : 'menu-item-wrap'}
              style={{
                backgroundColor:
                  this.state.isActive === 'summaryFuelUsage' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="summaryFuelUsage"
                permissions={permissions}
              >
                <Link to="/report/summary-fuel-usage">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.s.summaryVehicleUsage}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* น้ำมันคงเหลือ */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="fuelremainrateReport"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              className={collapsed ? '' : 'menu-item-wrap'}
              style={{
                backgroundColor:
                  this.state.isActive === 'fuelremainrateReport' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="fuelremainrateReport"
                permissions={permissions}
              >
                <Link to="/report/fuelremainrate">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.f.fuelremainrateReport}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            <Menu.Item
              className={collapsed ? '' : 'menu-item-wrap'}
              key={autoGenKey('slider-sub-menu')}
              matching_name="FueluseReport"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'FueluseReport' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="FueluseReport"
                permissions={permissions}
              >
                <Link to="/report/fueluse">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.f.FueluseReport}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>
          </Menu.SubMenu>

          {/* ค่าใช้จ่าย GPS */}
          <Menu.SubMenu
            popupClassName="popup-menu-custom scroll-style"
            key={'theCost_1'}
            menu_hierarchy="theCost"
            permissions={permissions}
            title={
              <LangContext.Consumer>
                {i18n => i18n.t.theCost}
              </LangContext.Consumer>
            }
          >
            {/* ประสิทธิภาพ GPS */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="gpsPerformanceDashboard"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'gpsPerformanceDashboard' &&
                  '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="gpsPerformanceDashboard"
                permissions={permissions}
              >
                <Link to="/gps_performance_dashboard">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.g.gpsPerformanceDashboard}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* สรุปค่าบริการ GPS ทั้งหมด */}
            <Menu.Item
              className={collapsed ? '' : 'menu-item-wrap'}
              key={autoGenKey('slider-sub-menu')}
              matching_name="gpsVendorPayingSummaryReport"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'gpsVendorPayingSummaryReport' &&
                  '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="gpsVendorPayingSummaryReport"
                permissions={permissions}
              >
                <Link to="/report/gpsvendorpayingsummaryreport">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.g.gpsVendorPayingSummaryReport}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>
            {/* สรุปเรียกเก็บเงินตามผู้รับเหมา */}
            <Menu.Item
              className={collapsed ? '' : 'menu-item-wrap'}
              key={autoGenKey('slider-sub-menu')}
              matching_name="partnerPerformReport"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'partnerPerformReport' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="partnerPerformReport"
                permissions={permissions}
              >
                <Link to="/report/partnerperformreport">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.p.partnerPerformReport}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* สรุปค่าบริการตาม GPS Vendor */}
            <Menu.Item
              className={collapsed ? '' : 'menu-item-wrap'}
              key={autoGenKey('slider-sub-menu')}
              matching_name="gpsVendorPayingReport"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'gpsVendorPayingReport' &&
                  '#1890FF70',
              }}
              // report_repairing
            >
              <CustomMenuItem
                matching_name="gpsVendorPayingReport"
                permissions={permissions}
              >
                <Link to="/report/gpsvendorpayingreport">
                  <span className="nav-text">
                    {' '}
                    <LangContext.Consumer>
                      {i18n => i18n.g.gpsVendorPayingReport}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>
          </Menu.SubMenu>

          {/* การจัดการอุปกรณ์ */}
          <Menu.SubMenu
            popupClassName="popup-menu-custom scroll-style"
            key={'maintenancedevice_report_menu_1'}
            menu_hierarchy="maintenanceDevice"
            permissions={permissions}
            title={
              <LangContext.Consumer>
                {i18n => i18n.m.maintenancedevice_report_menu}
              </LangContext.Consumer>
            }
          >
            {/* การแจ้งซ่อม */}
            <Menu.Item
              className={collapsed ? '' : 'menu-item-wrap'}
              key={autoGenKey('slider-sub-menu')}
              matching_name="repairingReport"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'repairingReport' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="repairingReport"
                permissions={permissions}
              >
                <Link to="/report/repairingreport">
                  <span className="nav-text">
                    {' '}
                    <LangContext.Consumer>
                      {i18n => i18n.r.repairingReport}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* GPS ขาดการติดต่อ */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="gpsReportLostContact"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              className={collapsed ? '' : 'menu-item-wrap'}
              style={{
                backgroundColor:
                  this.state.isActive === 'gpsReportLostContact' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="gpsReportLostContact"
                permissions={permissions}
              >
                <Link to="/report/gps-lost-contact">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.g.gpsReportLostContact}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="gpsDisconnectOnTrip"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              className={collapsed ? '' : 'menu-item-wrap'}
              style={{
                backgroundColor:
                  this.state.isActive === 'gpsDisconnectOnTrip' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="gpsDisconnectOnTrip"
                permissions={permissions}
              >
                <Link to="/report/gps-disconnect-on-trip">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.g.gpsDisconnectOnTrip}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>
          </Menu.SubMenu>
        </AutorizeMenu>

        {/* Admin GPS */}
        <AutorizeMenu
          isActive={this.state.isActive}
          handleActiveMenu={this.handleActiveMenu}
          permissions={permissions}
          title={
            <span>
              <Icon
                type="global"
                style={{ fontSize: 'larger', color: '#1890FF' }}
              />
              <span>Admin GPS</span>
            </span>
          }
          subkey={'admin-gps-submenu'}
          handleonOpenChange={this.onOpenChange}
          openKeysMenu={this.state.openKeys}
          menu_hierarchy="adminGPS"
          createExternalMenu={this.createExternalMenu}
        >
          {/* แจ้งติดตั้งอุปกรณ์ GPS */}
          <Menu.Item
            key={autoGenKey('slider-sub-menu')}
            matching_name="gpsInstallation"
            web_endpoint={this.state.web_endpoint}
            web_use=""
            style={{
              backgroundColor:
                this.state.isActive === 'gpsInstallation' && '#1890FF70',
            }}
          >
            <Link to="/gps-installation">
              <span className="nav-text">
                <LangContext.Consumer>
                  {i18n => i18n.g.gpsInstallation}
                </LangContext.Consumer>
              </span>
              {countGPSServiceRequestsInstall != 0 &&
                checkPermissionShowCountGPSServiceRequestsInstall !=
                  undefined &&
                (checkPermissionShowCountGPSServiceRequestsInstall.can_view ==
                1 ? (
                  <sup
                    style={{
                      color: 'white',
                      margin: 5,
                      backgroundColor: '#1890FF',
                      borderRadius: 12,
                    }}
                  >
                    &nbsp; {countGPSServiceRequestsInstall} &nbsp;
                  </sup>
                ) : null)}
            </Link>
          </Menu.Item>

          {/* แจ้งซ่อมอุปกรณ์ GPS */}
          <Menu.Item
            key={autoGenKey('slider-sub-menu')}
            matching_name="gpsRepair"
            web_endpoint={this.state.web_endpoint}
            web_use=""
            style={{
              backgroundColor:
                this.state.isActive === 'gpsRepair' && '#1890FF70',
            }}
          >
            <Link to="/gps-repair">
              <span className="nav-text">
                <LangContext.Consumer>
                  {i18n => i18n.g.gpsRepair}
                </LangContext.Consumer>
              </span>
              {countGPSServiceRequestsRepair != 0 &&
                checkPermissionShowCountGPSServiceRequestsRepair != undefined &&
                (checkPermissionShowCountGPSServiceRequestsRepair.can_view ==
                1 ? (
                  <sup
                    style={{
                      color: 'white',
                      margin: 5,
                      backgroundColor: '#1890FF',
                      borderRadius: 12,
                    }}
                  >
                    &nbsp; {countGPSServiceRequestsRepair} &nbsp;
                  </sup>
                ) : null)}
            </Link>
          </Menu.Item>

          {/* แจ้งถอด GPS */}
          <Menu.Item
            key={autoGenKey('slider-sub-menu')}
            matching_name="gpsUninstallation"
            web_endpoint={this.state.web_endpoint}
            web_use=""
            style={{
              backgroundColor:
                this.state.isActive === 'gpsUninstallation' && '#1890FF70',
            }}
          >
            <Link to="/gps-uninstallation">
              <span className="nav-text">
                <LangContext.Consumer>
                  {i18n => i18n.g.gpsUninstallation}
                </LangContext.Consumer>
              </span>
              {countGPSServiceRequestsUninstall != 0 &&
                checkPermissionShowCountGPSServiceRequestsUninstall !=
                  undefined &&
                (checkPermissionShowCountGPSServiceRequestsUninstall.can_view ==
                1 ? (
                  <sup
                    style={{
                      color: 'white',
                      margin: 5,
                      backgroundColor: '#1890FF',
                      borderRadius: 12,
                    }}
                  >
                    &nbsp; {countGPSServiceRequestsUninstall} &nbsp;
                  </sup>
                ) : null)}
            </Link>
          </Menu.Item>

          {/* ประสิทธิภาพอุปกรณ์รายเดือน */}
          <Menu.Item
            key={autoGenKey('slider-sub-menu')}
            matching_name="gpsMonthlyUsage"
            web_endpoint={this.state.web_endpoint}
            web_use=""
            style={{
              backgroundColor:
                this.state.isActive === 'gpsMonthlyUsage' && '#1890FF70',
            }}
          >
            <Link to="/gps-monthly-usage">
              <span className="nav-text">
                <LangContext.Consumer>
                  {i18n => i18n.g.gpsMonthlyUsage}
                </LangContext.Consumer>
              </span>
              {countGpsMonthlyUsage != 0 &&
                checkPermissionShowCountGpsMonthlyUsage != undefined &&
                (checkPermissionShowCountGpsMonthlyUsage.can_view == 1 ? (
                  <sup
                    style={{
                      color: 'white',
                      margin: 5,
                      backgroundColor: '#1890FF',
                      borderRadius: 12,
                    }}
                  >
                    &nbsp; {countGpsMonthlyUsage} &nbsp;
                  </sup>
                ) : null)}
            </Link>
          </Menu.Item>

          {/* รายงาน Admin GPS */}
          <Menu.SubMenu
            popupClassName="popup-menu-custom scroll-style"
            key={'report-admin-gps-sub-menu'}
            menu_hierarchy="report"
            permissions={permissions}
            title={
              <LangContext.Consumer>
                {i18n => i18n.r.report}
              </LangContext.Consumer>
            }
          >
            {/* cpac_operation_contractor */}
            <Menu.Item
              className={collapsed ? '' : 'menu-item-wrap'}
              key={autoGenKey('slider-sub-menu')}
              matching_name="cpac_operation_contractor"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'cpac_operation_contractor' &&
                  '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="cpac_operation_contractor"
                permissions={permissions}
              >
                <Link to="/report/cpac-operation-contractor">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.c.cpac_operation_contractor}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* cpac_operation_chain */}
            <Menu.Item
              className={collapsed ? '' : 'menu-item-wrap'}
              key={autoGenKey('slider-sub-menu')}
              matching_name="cpac_operation_chain"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'cpac_operation_chain' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="cpac_operation_chain"
                permissions={permissions}
              >
                <Link to="/report/cpac-operation-chain">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.c.cpac_operation_chain}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* cpac_operation_vendor */}
            <Menu.Item
              className={collapsed ? '' : 'menu-item-wrap'}
              key={autoGenKey('slider-sub-menu')}
              matching_name="cpac_operation_vendor"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'cpac_operation_vendor' &&
                  '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="cpac_operation_vendor"
                permissions={permissions}
              >
                <Link to="/report/cpac-operation-vendor">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.c.cpac_operation_vendor}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>
          </Menu.SubMenu>

          {/* ตั้งค่า Operation Data */}
          <Menu.SubMenu
            popupClassName="popup-menu-custom scroll-style"
            key={'operation_data_setting'}
            menu_hierarchy="setting"
            permissions={permissions}
            title={
              <LangContext.Consumer>
                {i18n => i18n.s.setting}
              </LangContext.Consumer>
            }
          >
            {/* ตั้งค่ารหัสทางบัญชี กิจการ/ฝ่ายส่วน */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="departmentCostSetup"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'departmentCostSetup' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="departmentCostSetup"
                permissions={permissions}
              >
                <Link to="/department-cost-setup">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.d.departmentCostSetup}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* ตั้งค่ารหัสทางบัญชีทีม CPAC */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="accountingCodeSetup"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'accountingCodeSetup' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="accountingCodeSetup"
                permissions={permissions}
              >
                <Link to="/accounting-code-setup">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.a.accountingCodeSetup}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* ตั้งค่ารายการอุปกรณ์ GPS*/}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="gpsDeviceListSetup"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'gpsDeviceListSetup' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="gpsDeviceListSetup"
                permissions={permissions}
              >
                <Link to="/gps-device-list-setup">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.g.gpsDeviceListSetup}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* ตั้งค่าราคาอุปกรณ์ GPS */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="gpsVendorCostSetup"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'gpsVendorCostSetup' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="gpsVendorCostSetup"
                permissions={permissions}
              >
                <Link to="/gps-vendor-cost-setup">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.g.gpsVendorCostSetup}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* ตั้งค่าผู้รับผิดชอบค่าใช้จ่าย */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="masterPaymentSetup"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'masterPaymentSetup' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="masterPaymentSetup"
                permissions={permissions}
              >
                <Link to="/master-payment-setup">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.m.masterPaymentSetup}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* ตั้งค่าข้อมูลผู้รับเหมา */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="gpsContractorSetup"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'gpsContractorSetup' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="gpsContractorSetup"
                permissions={permissions}
              >
                <Link to="/gps-contractor-setup">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.g.gpsContractorSetup}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* ข้อมูลการติดตั้ง GPS */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="gpsDeviceTracker"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'gpsDeviceTracker' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="gpsDeviceTracker"
                permissions={permissions}
              >
                <Link to="/gps-device-tracker">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.g.gpsDeviceTracker}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>
          </Menu.SubMenu>
        </AutorizeMenu>

        {/* Route Optimize */}
        <AutorizeMenu
          isActive={this.state.isActive}
          handleActiveMenu={this.handleActiveMenu}
          permissions={permissions}
          title={
            <span>
              <Icon
                type="audit"
                style={{ fontSize: 'larger', color: '#1890FF' }}
              />
              <span>Route Optimize</span>
            </span>
          }
          subkey={'route-optimize-submenu'}
          handleonOpenChange={this.onOpenChange}
          openKeysMenu={this.state.openKeys}
          menu_hierarchy="routeOptimize"
          createExternalMenu={this.createExternalMenu}
        >
          <Menu.Item
            key={autoGenKey('slider-sub-menu')}
            matching_name="dashboard-qtft"
            web_endpoint={this.state.web_endpoint}
            web_use=""
            style={{
              backgroundColor:
                this.state.isActive === 'dashboard-qtft' && '#1890FF70',
            }}
          >
            <CustomMenuItem
              matching_name="dashboard-qtft"
              permissions={permissions}
            >
              <Link to="/dashboard-qtft">
                <span className="nav-text">Dashboard QTFT</span>
              </Link>
            </CustomMenuItem>
          </Menu.Item>
          {/* แผนบันทึกการใช้รถโม่เล็ก */}
          <Menu.Item
            key={autoGenKey('slider-sub-menu')}
            matching_name="smartDispatching"
            web_endpoint={this.state.web_endpoint}
            web_use=""
            style={{
              backgroundColor:
                this.state.isActive === 'smartDispatching' && '#1890FF70',
            }}
          >
            <CustomMenuItem
              matching_name="smartDispatching"
              permissions={permissions}
            >
              <Link to="/smartDispatching">
                <span className="nav-text font_menu_item_slider_bar">
                  <LangContext.Consumer>
                    {i18n => i18n.s.smartDispatching}
                  </LangContext.Consumer>
                </span>
              </Link>
            </CustomMenuItem>
          </Menu.Item>
          {/* ขอใช้งานพาหนะ */}
          <Menu.SubMenu
            popupClassName="popup-menu-custom scroll-style"
            key={'request_to_use_vehicle'}
            menu_hierarchy="truckSharing"
            permissions={permissions}
            title={
              <>
                Truck Sharing
                {localStorage.getItem('count-appove-request-vehicle') != 0 &&
                  checkPermissionShowCountNoti != undefined &&
                  (checkPermissionShowCountNoti.can_view == 1 ? (
                    <sup
                      style={{
                        color: 'white',
                        margin: 5,
                        backgroundColor: '#1890FF',
                        borderRadius: 12,
                      }}
                    >
                      &nbsp; {countAppoveRequest} &nbsp;
                    </sup>
                  ) : null)}
              </>
            }
          >
            {/* รายการพาหนะบัญชี 2 */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="accountVehicle2"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'accountVehicle2' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="accountVehicle2"
                permissions={permissions}
              >
                <Link to="/list-of-vehicle-account">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.a.accountVehicle2}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* อนุมัติการขอใช้งานรถ */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="appoveToUseVehicle"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'appoveToUseVehicle' && '#1890FF70',
              }}
            >
              <Link to="/approve-request-to-use-vehicle">
                <span className="nav-text">
                  <LangContext.Consumer>
                    {i18n => i18n.a.appoveToUseVehicle}
                  </LangContext.Consumer>
                  {localStorage.getItem('count-appove-request-vehicle') != 0 &&
                    checkPermissionShowCountNoti != undefined &&
                    (checkPermissionShowCountNoti.can_view == 1 ? (
                      <sup
                        style={{
                          color: 'white',
                          margin: 5,
                          backgroundColor: '#1890FF',
                          borderRadius: 12,
                        }}
                      >
                        &nbsp; {countAppoveRequest} &nbsp;
                      </sup>
                    ) : null)}
                </span>
              </Link>
            </Menu.Item>

            {/* การจัดการรถของผู้รับเหมา */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="contractorVehicleManagement"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'contractorVehicleManagement' &&
                  '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="contractorVehicleManagement"
                permissions={permissions}
              >
                <Link to="/contractor-vehicle-management">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.c.contractorVehicleManagement}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* ประวัติรายการพาหนะบัญชี 2 */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="accountVehicleListHistory"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'accountVehicleListHistory' &&
                  '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="accountVehicleListHistory"
                permissions={permissions}
              >
                <Link to="/account-vehicle-list-history">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.a.accountVehicleListHistory}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>
          </Menu.SubMenu>
        </AutorizeMenu>

        {/* ตั้งค่า  */}
        <AutorizeMenu
          isActive={this.state.isActive}
          handleActiveMenu={this.handleActiveMenu}
          permissions={permissions}
          title={
            <span>
              <Icon
                type="setting"
                style={{ fontSize: 'larger', color: '#1890FF' }}
              />
              <span>
                {' '}
                <LangContext.Consumer>
                  {i18n => i18n.s.setting}
                </LangContext.Consumer>
              </span>
            </span>
          }
          handleonOpenChange={this.onOpenChange}
          openKeysMenu={this.state.openKeys}
          subkey={'setting_submenu'}
          menu_hierarchy="setting"
          createExternalMenu={this.createExternalMenu}
        >
          {/* ข้อมูลผู้ใช้งาน */}
          <Menu.SubMenu
            popupClassName="popup-menu-custom scroll-style"
            key={'setting-user-information-sub-menu'}
            menu_hierarchy="userInformation"
            permissions={permissions}
            title={
              <LangContext.Consumer>
                {i18n => i18n.i.isupplyCustomerInformation}
              </LangContext.Consumer>
            }
          >
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="viewuser_perm"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'viewuserpermission' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="viewuser_perm"
                permissions={permissions}
              >
                <Link to="/viewuserpermission">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.d.dataVisibility}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>
            {/* vehicleVisibilityPermission */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="vehicleVisibilityPermission"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'vehicleVisibilityPermission' &&
                  '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="vehicleVisibilityPermission"
                permissions={permissions}
              >
                <Link to="/vehicleVisibilityPermission">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.vehicleVisibilityPermission.generalData}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* Permission */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="permission"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'permission' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="permission"
                permissions={permissions}
              >
                <Link to="/permission">
                  <span className="nav-text">Permission</span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* ผู้ใช้งาน */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="user"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor: this.state.isActive === 'user' && '#1890FF70',
              }}
            >
              <CustomMenuItem matching_name="user" permissions={permissions}>
                <Link to="/user">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.u.user}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* กลุ่มผู้ใช้งาน */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="userGroup"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'userGroup' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="userGroup"
                permissions={permissions}
              >
                <Link to="/usergroup">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.u.userGroup}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* การมองเห็นข้อมูล */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="dataVisibility"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'dataVisibility' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="dataVisibility"
                permissions={permissions}
              >
                <Link to="/datavisibility">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.d.dataVisibility}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>
            {/* Generate Token Account*/}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="generateToken"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'generateToken' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="generateToken"
                permissions={permissions}
              >
                <Link to="/generateusertoken">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.g.generateTokenAccount}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>
          </Menu.SubMenu>

          {/* ตั้งค่าสถานที่ */}
          <Menu.SubMenu
            popupClassName="popup-menu-custom scroll-style"
            key={'setting-locations-sub-menu'}
            menu_hierarchy="locations"
            permissions={permissions}
            title={
              <LangContext.Consumer>
                {i18n => i18n.l.location}
              </LangContext.Consumer>
            }
          >
            {/* สถานที่่ */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="location"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'location' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="location"
                permissions={permissions}
              >
                <Link to="/location">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.l.Location}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>
            {/* สถานที่่(layer-map) */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="layer-map"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'layer-map' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="layer-map"
                permissions={permissions}
              >
                <Link to="/layer-map">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.l.Location}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>
            {/* สถานที่ใกล้เคียง */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="nearByLocation"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'nearByLocation' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="nearByLocation"
                permissions={permissions}
              >
                <Link to="/nearbylocation">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.n.nearByLocation}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* กลุ่มสถานที่ */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="locationGroup"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'locationGroup' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="locationGroup"
                permissions={permissions}
              >
                <Link to="/locationgroup">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.l.locationGroup}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* กลุ่มหน่วยงาน */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="siteGroup"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'siteGroup' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="siteGroup"
                permissions={permissions}
              >
                <Link to="/sitegroup">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.s.siteGroup}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="subPlant"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'subPlant' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="subPlant"
                permissions={permissions}
              >
                <Link to="/sublocation">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.s.subPlant}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>
          </Menu.SubMenu>

          {/* พาหนะ */}
          <Menu.SubMenu
            popupClassName="popup-menu-custom scroll-style"
            key={'vehicle_1'}
            menu_hierarchy="vehicle"
            permissions={permissions}
            title={
              <LangContext.Consumer>
                {i18n => i18n.v.vehicle}
              </LangContext.Consumer>
            }
          >
            {/* แชร์พาหนะ */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="vehicleSharing"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'vehicleSharing' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="vehicleSharing"
                permissions={permissions}
              >
                <Link to="/vehicle_sharing">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.v.vehicleSharing}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* ประเภทพาหนะ */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="vehicle_type"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'vehicle_type' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="vehicle_type"
                permissions={permissions}
              >
                <Link to="/vehicle-type">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.v.vehicle_type}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* พาหนะ */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="vehicle"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'vehicle' && '#1890FF70',
              }}
            >
              <CustomMenuItem matching_name="vehicle" permissions={permissions}>
                <Link to="/vehicle">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.v.vehicle}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* กลุ่มพาหนะ */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="vehicleGroup"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'vehicleGroup' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="vehicleGroup"
                permissions={permissions}
              >
                <Link to="/vehiclegroup">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.v.vehicleGroup}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>
          </Menu.SubMenu>

          {/* ตั้งค่า Help */}
          <Menu.SubMenu
            popupClassName="popup-menu-custom scroll-style"
            key={'setting-help-menu-sub-menu'}
            menu_hierarchy="help"
            permissions={permissions}
            title={
              <LangContext.Consumer>
                {i18n => i18n.h.help_menu}
              </LangContext.Consumer>
            }
          >
            {/* ขอใบรับรอง */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              // key={'item_help_menu_1'}
              matching_name="requestACertificate"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'requestACertificate' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="requestACertificate"
                permissions={permissions}
              >
                <Link to="/cpac-requestcertificate">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.r.requestACertificate}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>

            {/* แจ้งซ่อมอุปกรณ์ */}
            <Menu.Item
              key={autoGenKey('slider-sub-menu')}
              matching_name="maintenancedevice"
              web_endpoint={this.state.web_endpoint}
              web_use=""
              style={{
                backgroundColor:
                  this.state.isActive === 'maintenancedevice' && '#1890FF70',
              }}
            >
              <CustomMenuItem
                matching_name="maintenancedevice"
                permissions={permissions}
              >
                <Link to="/maintenancedevice">
                  <span className="nav-text">
                    <LangContext.Consumer>
                      {i18n => i18n.m.maintenancedevice}
                    </LangContext.Consumer>
                  </span>
                </Link>
              </CustomMenuItem>
            </Menu.Item>
          </Menu.SubMenu>

          {/* อัพเดท imei */}
          <Menu.Item
            key={autoGenKey('slider-sub-menu')}
            matching_name="updateimei"
            web_endpoint={this.state.web_endpoint}
            web_use=""
            style={{
              backgroundColor:
                this.state.isActive === 'updateimei' && '#1890FF70',
            }}
          >
            <CustomMenuItem
              matching_name="updateimei"
              permissions={permissions}
            >
              <Link to="/updateimei">
                <span className="nav-text">
                  <LangContext.Consumer>
                    {i18n => i18n.u.updateimei}
                  </LangContext.Consumer>
                </span>
              </Link>
            </CustomMenuItem>
          </Menu.Item>
          {/* vehicleVisibilityPermissionGeneralData */}
          <Menu.Item
            key={autoGenKey('slider-sub-menu')}
            matching_name="vehicleVisibilityPermissionGeneralData"
            web_endpoint={this.state.web_endpoint}
            web_use=""
            style={{
              backgroundColor:
                this.state.isActive ===
                  'vehicleVisibilityPermissionGeneralData' && '#1890FF70',
            }}
          >
            <CustomMenuItem
              matching_name="vehicleVisibilityPermissionGeneralData"
              permissions={permissions}
            >
              <Link to="/vehicleVisibilityPermissionGeneralData">
                <span className="nav-text">
                  <LangContext.Consumer>
                    {i18n =>
                      i18n.vehicleVisibilityPermission
                        .vehicleVisibilityPermissionHeader
                    }
                  </LangContext.Consumer>
                </span>
              </Link>
            </CustomMenuItem>
          </Menu.Item>
          {/* ลายเซ็น */}
          <Menu.Item
            key={autoGenKey('slider-sub-menu')}
            matching_name="signatureImage"
            web_endpoint={this.state.web_endpoint}
            web_use=""
            style={{
              backgroundColor:
                this.state.isActive === 'signatureImage' && '#1890FF70',
            }}
          >
            <CustomMenuItem
              matching_name="signatureImage"
              permissions={permissions}
            >
              <Link to="/signature-image">
                <span className="nav-text">
                  <LangContext.Consumer>
                    {i18n => i18n.s.signatureImage}
                  </LangContext.Consumer>
                </span>
              </Link>
            </CustomMenuItem>
          </Menu.Item>

          <Menu.Item
            key={autoGenKey('slider-sub-menu')}
            matching_name="company"
            web_endpoint={this.state.web_endpoint}
            web_use=""
            style={{
              backgroundColor: this.state.isActive === 'company' && '#1890FF70',
            }}
          >
            <CustomMenuItem matching_name="company" permissions={permissions}>
              <Link to="/company">
                <span className="nav-text">
                  <LangContext.Consumer>
                    {i18n => i18n.c.company}
                  </LangContext.Consumer>
                </span>
              </Link>
            </CustomMenuItem>
          </Menu.Item>

          <Menu.Item
            key={autoGenKey('slider-sub-menu')}
            matching_name="dynamicSidebar"
            web_endpoint={this.state.web_endpoint}
            web_use=""
            style={{
              backgroundColor:
                this.state.isActive === 'dynamicSidebar' && '#1890FF70',
            }}
          >
            <CustomMenuItem
              matching_name="dynamicSidebar"
              permissions={permissions}
            >
              <Link to="/dynamic-sidebar">
                <span className="nav-text">
                  <LangContext.Consumer>
                    {i18n => i18n.d.dynamicSidebar}
                  </LangContext.Consumer>
                </span>
              </Link>
            </CustomMenuItem>
          </Menu.Item>

          {/* interface monitoring */}
          <Menu.Item
            key={autoGenKey('slider-sub-menu')}
            matching_name="interface-monitoring"
            web_endpoint={this.state.web_endpoint}
            web_use=""
            style={{
              backgroundColor:
                this.state.isActive === 'interface-monitoring' && '#1890FF70',
            }}
          >
            <CustomMenuItem
              matching_name="interface-monitoring"
              permissions={permissions}
            >
              <Link to="/monitoring/interface-monitoring">
                <span className="nav-text">Interface Monitoring</span>
              </Link>
            </CustomMenuItem>
          </Menu.Item>

          {/* SCCO Webservice Log */}
          <Menu.Item
            key={autoGenKey('slider-sub-menu')}
            matching_name="sccoWebserviceLog"
            web_endpoint={this.state.web_endpoint}
            web_use=""
            style={{
              backgroundColor:
                this.state.isActive === 'sccoWebserviceLog' && '#1890FF70',
            }}
          >
            <CustomMenuItem
              matching_name="sccoWebserviceLog"
              permissions={permissions}
            >
              <Link to="/webservice-log">
                <span className="nav-text">Webservice Log</span>
              </Link>
            </CustomMenuItem>
          </Menu.Item>

          {/* Notification Setting */}
          <Menu.Item
            key={autoGenKey('slider-sub-menu')}
            matching_name="notificationSetting"
            web_endpoint={this.state.web_endpoint}
            web_use=""
            style={{
              backgroundColor:
                this.state.isActive === 'notificationSetting' && '#1890FF70',
            }}
          >
            <CustomMenuItem
              matching_name="notificationSetting"
              permissions={permissions}
            >
              <Link to="/notification-setting">
                <span className="nav-text">
                  <LangContext.Consumer>
                    {i18n => i18n.n.notificationSetting}
                  </LangContext.Consumer>
                </span>
              </Link>
            </CustomMenuItem>
          </Menu.Item>

          <Menu.Item
            key={autoGenKey('slider-sub-menu')}
            matching_name="linenotify"
            web_endpoint={this.state.web_endpoint}
            web_use=""
            style={{
              backgroundColor:
                this.state.isActive === 'linenotify' && '#1890FF70',
            }}
          >
            <CustomMenuItem
              matching_name="linenotify"
              permissions={permissions}
            >
              <Link to="/linenotify">
                <span className="nav-text">
                  <LangContext.Consumer>
                    {i18n => i18n.l.linenotify}
                  </LangContext.Consumer>
                </span>
              </Link>
            </CustomMenuItem>
          </Menu.Item>

          <Menu.Item
            key={autoGenKey('slider-sub-menu')}
            matching_name="policy"
            web_endpoint={this.state.web_endpoint}
            web_use=""
            style={{
              backgroundColor: this.state.isActive === 'policy' && '#1890FF70',
            }}
          >
            <CustomMenuItem matching_name="policy" permissions={permissions}>
              <Link to="/policy">
                <span className="nav-text">
                  <LangContext.Consumer>
                    {i18n => i18n.p.policy}
                  </LangContext.Consumer>
                </span>
              </Link>
            </CustomMenuItem>
          </Menu.Item>
        </AutorizeMenu>

        {SHOW_LEGACY_MENU && (
          <>
            {/* เมนู Legacy (ไม่ใช้แล้ว) */}
            <AutorizeMenu
              isActive={this.state.isActive}
              handleActiveMenu={this.handleActiveMenu}
              permissions={permissions}
              title={
                <span>
                  <Icon
                    type="folder-open"
                    style={{ fontSize: 'larger', color: '#1890FF' }}
                  />
                  <span> เมนู Legacy (ไม่ใช้แล้ว)</span>
                </span>
              }
              subkey={'legacy-sub-menu'}
              menu_hierarchy="legacy"
              handleonOpenChange={this.onOpenChange}
              openKeysMenu={this.state.openKeys}
            >
              {/* Dashboard/Monitoring legacy */}
              <Menu.SubMenu
                popupClassName="popup-menu-custom scroll-style"
                key={'legacy_dashboard'}
                menu_hierarchy="dashboard"
                permissions={permissions}
                title={<span>Dashboard (Legacy)</span>}
              >
                {/* Dashboard/Monitoring legacy */}
                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="notifyTheTruckInCorrectPoint"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  style={{
                    backgroundColor:
                      this.state.isActive === 'notifyTheTruckInCorrectPoint' &&
                      '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="notifyTheTruckInCorrectPoint"
                    permissions={permissions}
                  >
                    <Link to="/truckincorrectpoint">
                      <span className="nav-text font_menu_item_slider_bar">
                        <LangContext.Consumer>
                          {i18n => i18n.n.notifyTheTruckInCorrectPoint}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="alert_alarm_dashboard"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  style={{
                    backgroundColor:
                      this.state.isActive === 'alert_alarm_dashboard' &&
                      '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="alert_alarm_dashboard"
                    permissions={permissions}
                  >
                    <Link to="/alert_alarm">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.a.alertAlarm}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  className={collapsed ? '' : 'menu-item-wrap'}
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="eventMap"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  style={{
                    backgroundColor:
                      this.state.isActive === 'eventMap' && '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="eventMap"
                    permissions={permissions}
                  >
                    <Link to="/event_map">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.e.eventMap}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="summary_dashboard"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  style={{
                    backgroundColor:
                      this.state.isActive === 'summary_dashboard' &&
                      '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="summary_dashboard"
                    permissions={permissions}
                  >
                    <Link to="/summary_dashboard">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.s.SafetyDashboard}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="booking_overview_dashboard"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  style={{
                    backgroundColor:
                      this.state.isActive === 'booking_overview_dashboard' &&
                      '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="booking_overview_dashboard"
                    permissions={permissions}
                  >
                    <Link to="/booking_overview">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.b.bookingOverview}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="cashing_overview_dahsboar"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  style={{
                    backgroundColor:
                      this.state.isActive === 'cashing_overview_dahsboar' &&
                      '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="cashing_overview_dahsboar"
                    permissions={permissions}
                  >
                    <Link to="/cashing_overview">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.e.earningOverview}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>
              </Menu.SubMenu>

              {/* Master/Management legacy */}
              <Menu.SubMenu
                popupClassName="popup-menu-custom scroll-style"
                key={'legacy_master'}
                menu_hierarchy="masterdata"
                permissions={permissions}
                title={<span>Master/Management (Legacy)</span>}
              >
                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="zone"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  style={{
                    backgroundColor:
                      this.state.isActive === 'zone' && '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="zone"
                    permissions={permissions}
                  >
                    <Link to="/zone">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.z.zone}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="customer"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  style={{
                    backgroundColor:
                      this.state.isActive === 'customer' && '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="customer"
                    permissions={permissions}
                  >
                    <Link to="/customer">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.c.customer}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="product"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  style={{
                    backgroundColor:
                      this.state.isActive === 'product' && '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="product"
                    permissions={permissions}
                  >
                    <Link to="/product">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.p.product}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="uom"
                  style={{
                    backgroundColor:
                      this.state.isActive === 'uom' && '#1890FF70',
                  }}
                >
                  <CustomMenuItem matching_name="uom" permissions={permissions}>
                    <Link to="/uom">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.u.uom}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="vehicleContract"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  style={{
                    backgroundColor:
                      this.state.isActive === 'vehicleContract' && '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="vehicleContract"
                    permissions={permissions}
                  >
                    <Link to="/vehicle-contract">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.vehicleContract.vehicleContract}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  className={collapsed ? '' : 'menu-item-wrap'}
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="chauffeur"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  style={{
                    backgroundColor:
                      this.state.isActive === 'chauffeur' && '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="chauffeur"
                    permissions={permissions}
                  >
                    <Link to="/deviceuser">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.c.chauffeur}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="maintenance"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  style={{
                    backgroundColor:
                      this.state.isActive === 'maintenance' && '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="maintenance"
                    permissions={permissions}
                  >
                    <Link to="/maintenance">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.m.maintenance}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="reFuelTitle"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  style={{
                    backgroundColor:
                      this.state.isActive === 'reFuelTitle' && '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="reFuelTitle"
                    permissions={permissions}
                  >
                    <Link to="/refuel">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.r.reFuelTitle}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="partnerCar"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  style={{
                    backgroundColor:
                      this.state.isActive === 'partnerCar' && '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="partnerCar"
                    permissions={permissions}
                  >
                    <Link to="/partner">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.p.partnerCar}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="sendemail"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  style={{
                    backgroundColor:
                      this.state.isActive === 'sendemail' && '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="sendemail"
                    permissions={permissions}
                  >
                    <Link to="/verifyemail">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.a.accountVerification}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>
              </Menu.SubMenu>

              {/* Operation legacy */}
              <Menu.SubMenu
                popupClassName="popup-menu-custom scroll-style"
                key={'legacy_operation'}
                menu_hierarchy="operation"
                permissions={permissions}
                title={<span>Operation (Legacy)</span>}
              >
                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="trip"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  style={{
                    backgroundColor:
                      this.state.isActive === 'trip' && '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="trip"
                    permissions={permissions}
                  >
                    <Link to="/trip">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.t.trip}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="routemaster"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  style={{
                    backgroundColor:
                      this.state.isActive === 'routemaster' && '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="routemaster"
                    permissions={permissions}
                  >
                    <Link to="/routemaster">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.r.routemaster}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="delivery_trip"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  style={{
                    backgroundColor:
                      this.state.isActive === 'delivery_trip' && '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="delivery_trip"
                    permissions={permissions}
                  >
                    <Link to="/deliverytrip">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.d.deliverytrip}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  className={collapsed ? '' : 'menu-item-wrap'}
                  matching_name="informationAboutTheCarRunWall"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  style={{
                    backgroundColor:
                      this.state.isActive === 'informationAboutTheCarRunWall' &&
                      '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="informationAboutTheCarRunWall"
                    permissions={permissions}
                  >
                    <Link to="/cpac-precast-transportation-information-wall">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.i.informationAboutTheCarRunWall}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  className={collapsed ? '' : 'menu-item-wrap'}
                  matching_name="informationAboutTheCarRunFloor"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  style={{
                    backgroundColor:
                      this.state.isActive ===
                        'informationAboutTheCarRunFloor' && '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="informationAboutTheCarRunFloor"
                    permissions={permissions}
                  >
                    <Link to="/cpac-precast-transportation-information-floor">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.i.informationAboutTheCarRunFloor}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="floorWallDelivery"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  className={collapsed ? '' : 'menu-item-wrap'}
                  style={{
                    backgroundColor:
                      this.state.isActive === 'floorWallDelivery' &&
                      '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="floorWallDelivery"
                    permissions={permissions}
                  >
                    <Link to="/precast-report">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.f.floorWallDelivery}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  className={collapsed ? '' : 'menu-item-wrap'}
                  matching_name="distance_report_1"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  style={{
                    backgroundColor:
                      this.state.isActive === 'distance_report_1' &&
                      '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="distance_report_1"
                    permissions={permissions}
                  >
                    <Link to="/distancereport">
                      <span className="nav-text">
                        {this.context.d.deliveryDistance}
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>
              </Menu.SubMenu>

              {/* Report legacy */}
              <Menu.SubMenu
                popupClassName="popup-menu-custom scroll-style"
                key={'legacy_report'}
                menu_hierarchy="report"
                permissions={permissions}
                title={<span>Report (Legacy)</span>}
              >
                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="TruckcurrentlocationReport"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  className={collapsed ? '' : 'menu-item-wrap'}
                  style={{
                    backgroundColor:
                      this.state.isActive === 'TruckcurrentlocationReport' &&
                      '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="TruckcurrentlocationReport"
                    permissions={permissions}
                  >
                    <Link to="/report/truckcurrentlocation">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.t.TruckcurrentlocationReport}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="DriverinformationReport"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  className={collapsed ? '' : 'menu-item-wrap'}
                  style={{
                    backgroundColor:
                      this.state.isActive === 'DriverinformationReport' &&
                      '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="DriverinformationReport"
                    permissions={permissions}
                  >
                    <Link to="/report/driverinformation">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.d.DriverinformationReport}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="GpsstatusReport"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  className={collapsed ? '' : 'menu-item-wrap'}
                  style={{
                    backgroundColor:
                      this.state.isActive === 'GpsstatusReport' && '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="GpsstatusReport"
                    permissions={permissions}
                  >
                    <Link to="/report/gpsstatus">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.g.GpsstatusReport}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="TrackingdataReport"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  className={collapsed ? '' : 'menu-item-wrap'}
                  style={{
                    backgroundColor:
                      this.state.isActive === 'TrackingdataReport' &&
                      '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="TrackingdataReport"
                    permissions={permissions}
                  >
                    <Link to="/report/trackingdata">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.t.TrackingdataReport}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="dailyDrivingBehaviorReport"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  className={collapsed ? '' : 'menu-item-wrap'}
                  style={{
                    backgroundColor:
                      this.state.isActive === 'dailyDrivingBehaviorReport' &&
                      '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="dailyDrivingBehaviorReport"
                    permissions={permissions}
                  >
                    <Link to="/report/drivingbehavior">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.d.dailyDrivingBehaviorReport}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="reportCardSwipeBehaviorBeforeDrivingDaily"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  style={{
                    backgroundColor:
                      this.state.isActive ===
                        'reportCardSwipeBehaviorBeforeDrivingDaily' &&
                      '#1890FF70',
                  }}
                  className={collapsed ? '' : 'menu-item-wrap'}
                >
                  <CustomMenuItem
                    matching_name="reportCardSwipeBehaviorBeforeDrivingDaily"
                    permissions={permissions}
                  >
                    <Link to="/report/drivinglicensescanbehavior">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n =>
                            i18n.r.reportCardSwipeBehaviorBeforeDrivingDaily
                          }
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="RiskbehaviourReport"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  className={collapsed ? '' : 'menu-item-wrap'}
                  style={{
                    backgroundColor:
                      this.state.isActive === 'RiskbehaviourReport' &&
                      '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="RiskbehaviourReport"
                    permissions={permissions}
                  >
                    <Link to="/report/riskbehaviour">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.r.RiskbehaviourReport}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="reportSummarizingTheNumberOfOffSiteParking"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  className={collapsed ? '' : 'menu-item-wrap'}
                  style={{
                    backgroundColor:
                      this.state.isActive ===
                        'reportSummarizingTheNumberOfOffSiteParking' &&
                      '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="reportSummarizingTheNumberOfOffSiteParking"
                    permissions={permissions}
                  >
                    <Link to="/report/parkingareaoutside">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n =>
                            i18n.r.reportSummarizingTheNumberOfOffSiteParking
                          }
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="VehicleinareaReport"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  className={collapsed ? '' : 'menu-item-wrap'}
                  style={{
                    backgroundColor:
                      this.state.isActive === 'VehicleinareaReport' &&
                      '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="VehicleinareaReport"
                    permissions={permissions}
                  >
                    <Link to="/report/vehicleinarea">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.v.VehicleinareaReport}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="DrivingoverlimitReport"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  className={collapsed ? '' : 'menu-item-wrap'}
                  style={{
                    backgroundColor:
                      this.state.isActive === 'DrivingoverlimitReport' &&
                      '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="DrivingoverlimitReport"
                    permissions={permissions}
                  >
                    <Link to="/report/drivingoverlimit">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.d.DrivingoverlimitReport}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="DozeoffriskReport"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  className={collapsed ? '' : 'menu-item-wrap'}
                  style={{
                    backgroundColor:
                      this.state.isActive === 'DozeoffriskReport' &&
                      '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="DozeoffriskReport"
                    permissions={permissions}
                  >
                    <Link to="/report/dozeoffrisk">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.d.DozeoffriskReport}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="SpeedoverlimitsummaryReport"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  className={collapsed ? '' : 'menu-item-wrap'}
                  style={{
                    backgroundColor:
                      this.state.isActive === 'SpeedoverlimitsummaryReport' &&
                      '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="SpeedoverlimitsummaryReport"
                    permissions={permissions}
                  >
                    <Link to="/report/speedoverlimitsummary">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.s.SpeedoverlimitsummaryReport}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="SummaryKPIReport"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  className={collapsed ? '' : 'menu-item-wrap'}
                  style={{
                    backgroundColor:
                      this.state.isActive === 'SummaryKPIReport' && '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="SummaryKPIReport"
                    permissions={permissions}
                  >
                    <Link to="/report/summarykpi">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.s.SummaryKPIReport}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="alarmAndAlertReportClassifiedByDailyCarRegistration"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  className={collapsed ? '' : 'menu-item-wrap'}
                  style={{
                    backgroundColor:
                      this.state.isActive ===
                        'alarmAndAlertReportClassifiedByDailyCarRegistration' &&
                      '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="alarmAndAlertReportClassifiedByDailyCarRegistration"
                    permissions={permissions}
                  >
                    <Link to="/report/alarmalertbyplateno">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n =>
                            i18n.a
                              .alarmAndAlertReportClassifiedByDailyCarRegistration
                          }
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="DailyAlarmAndAlertReportByDriver"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  className={collapsed ? '' : 'menu-item-wrap'}
                  style={{
                    backgroundColor:
                      this.state.isActive ===
                        'DailyAlarmAndAlertReportByDriver' && '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="DailyAlarmAndAlertReportByDriver"
                    permissions={permissions}
                  >
                    <Link to="/report/alarmalertbydriver">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.d.DailyAlarmAndAlertReportByDriver}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="alarmAndAlertReportClassifiedByYearlyCarRegistration"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  className={collapsed ? '' : 'menu-item-wrap'}
                  style={{
                    backgroundColor:
                      this.state.isActive ===
                        'alarmAndAlertReportClassifiedByYearlyCarRegistration' &&
                      '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="alarmAndAlertReportClassifiedByYearlyCarRegistration"
                    permissions={permissions}
                  >
                    <Link to="/report/alarmalertbyplatenoonyear">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n =>
                            i18n.a
                              .alarmAndAlertReportClassifiedByYearlyCarRegistration
                          }
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="reportAlarmFrequencyClassifiedByDailyDriver"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  className={collapsed ? '' : 'menu-item-wrap'}
                  style={{
                    backgroundColor:
                      this.state.isActive ===
                        'reportAlarmFrequencyClassifiedByDailyDriver' &&
                      '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="reportAlarmFrequencyClassifiedByDailyDriver"
                    permissions={permissions}
                  >
                    <Link to="/report/alarmalertfrequencybydriver">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n =>
                            i18n.r.reportAlarmFrequencyClassifiedByDailyDriver
                          }
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="top5AlertReportsOfEachTypeDividedByMonthlyTimePeriod"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  className={collapsed ? '' : 'menu-item-wrap'}
                  style={{
                    backgroundColor:
                      this.state.isActive ===
                        'top5AlertReportsOfEachTypeDividedByMonthlyTimePeriod' &&
                      '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="top5AlertReportsOfEachTypeDividedByMonthlyTimePeriod"
                    permissions={permissions}
                  >
                    <Link to="/report/alarmalertbydurationtime">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n =>
                            i18n.t
                              .top5AlertReportsOfEachTypeDividedByMonthlyTimePeriod
                          }
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="TrucknotuseReport"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  className={collapsed ? '' : 'menu-item-wrap'}
                  style={{
                    backgroundColor:
                      this.state.isActive === 'TrucknotuseReport' &&
                      '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="TrucknotuseReport"
                    permissions={permissions}
                  >
                    <Link to="/report/trucknotuse">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.t.TrucknotuseReport}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="TruckengineoffReport"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  className={collapsed ? '' : 'menu-item-wrap'}
                  style={{
                    backgroundColor:
                      this.state.isActive === 'TruckengineoffReport' &&
                      '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="TruckengineoffReport"
                    permissions={permissions}
                  >
                    <Link to="/report/truckengineoff">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.t.TruckengineoffReport}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="TripsummaryReport"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  className={collapsed ? '' : 'menu-item-wrap'}
                  style={{
                    backgroundColor:
                      this.state.isActive === 'TripsummaryReport' &&
                      '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="TripsummaryReport"
                    permissions={permissions}
                  >
                    <Link to="/report/tripsummary">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.t.TripsummaryReport}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="DailyReport"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  className={collapsed ? '' : 'menu-item-wrap'}
                  style={{
                    backgroundColor:
                      this.state.isActive === 'DailyReport' && '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="DailyReport"
                    permissions={permissions}
                  >
                    <Link to="/report/daily">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.d.DailyReport}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="TripsumbylocationReport"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  className={collapsed ? '' : 'menu-item-wrap'}
                  style={{
                    backgroundColor:
                      this.state.isActive === 'TripsumbylocationReport' &&
                      '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="TripsumbylocationReport"
                    permissions={permissions}
                  >
                    <Link to="/report/tripsumbylocation">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.t.TripsumbylocationReport}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="TripsumbyvehReport"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  className={collapsed ? '' : 'menu-item-wrap'}
                  style={{
                    backgroundColor:
                      this.state.isActive === 'TripsumbyvehReport' &&
                      '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="TripsumbyvehReport"
                    permissions={permissions}
                  >
                    <Link to="/report/tripsumbyveh">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.t.TripsumbyvehReport}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="EventReport"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  className={collapsed ? '' : 'menu-item-wrap'}
                  style={{
                    backgroundColor:
                      this.state.isActive === 'EventReport' && '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="EventReport"
                    permissions={permissions}
                  >
                    <Link to="/report/event">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.e.EventReport}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  className={collapsed ? '' : 'menu-item-wrap'}
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="RefuelReport"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  style={{
                    backgroundColor:
                      this.state.isActive === 'RefuelReport' && '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="RefuelReport"
                    permissions={permissions}
                  >
                    <Link to="/report/refuel">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.r.RefuelReport}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>

                <Menu.Item
                  className={collapsed ? '' : 'menu-item-wrap'}
                  key={autoGenKey('slider-sub-menu')}
                  matching_name="InsuranceReport"
                  web_endpoint={this.state.web_endpoint}
                  web_use=""
                  style={{
                    backgroundColor:
                      this.state.isActive === 'InsuranceReport' && '#1890FF70',
                  }}
                >
                  <CustomMenuItem
                    matching_name="InsuranceReport"
                    permissions={permissions}
                  >
                    <Link to="/report/insurance">
                      <span className="nav-text">
                        <LangContext.Consumer>
                          {i18n => i18n.i.InsuranceReport}
                        </LangContext.Consumer>
                      </span>
                    </Link>
                  </CustomMenuItem>
                </Menu.Item>
              </Menu.SubMenu>
              <Menu.Item
                key={autoGenKey('slider-sub-menu')}
                matching_name="servicerepairdevice"
                web_endpoint={this.state.web_endpoint}
                web_use=""
                style={{
                  backgroundColor:
                    this.state.isActive === 'servicerepairdevice' &&
                    '#1890FF70',
                }}
              >
                <CustomMenuItem
                  matching_name="servicerepairdevice"
                  permissions={permissions}
                >
                  <Link to="/servicerepairdevice">
                    <span className="nav-text">
                      <LangContext.Consumer>
                        {i18n => i18n.s.Servicerepairdevice}
                      </LangContext.Consumer>
                    </span>
                  </Link>
                </CustomMenuItem>
              </Menu.Item>

              <Menu.Item
                key={autoGenKey('slider-sub-menu')}
                matching_name="servicemovedevice"
                web_endpoint={this.state.web_endpoint}
                web_use=""
                style={{
                  backgroundColor:
                    this.state.isActive === 'servicemovedevice' && '#1890FF70',
                }}
              >
                <CustomMenuItem
                  matching_name="servicemovedevice"
                  permissions={permissions}
                >
                  <Link to="/servicemovedevice">
                    <span className="nav-text">
                      <LangContext.Consumer>
                        {i18n => i18n.m.moveDevice}
                      </LangContext.Consumer>
                    </span>
                  </Link>
                </CustomMenuItem>
              </Menu.Item>
            </AutorizeMenu>
          </>
        )}
      </Menu>
    )
  }
}

const mapStateToProps = ({ auth: { profile } }) => ({
  profile,
})

export default connect(mapStateToProps, null)(SliderBar)
